import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';

/**
 * Task type values.
 */
export const TaskType = {
  Approval: 'Approval',
  Review: 'Review',
  Action: 'Action',
  Information: 'Information',
} as const;

export type TaskType = typeof TaskType[keyof typeof TaskType];

/**
 * Task status values.
 */
export const TaskStatus = {
  Pending: 'Pending',
  InProgress: 'InProgress',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
  Completed: 'Completed',
} as const;

export type TaskStatus = typeof TaskStatus[keyof typeof TaskStatus];

/**
 * External task system DTO.
 */
export interface ExternalTaskSystemDto {
  id: string;
  tenantId: string;
  projectId?: string;
  name: string;
  description?: string;
  systemType: string;
  apiBaseUrl: string;
  isActive: boolean;
  taskCount: number;
}

/**
 * External task system detail DTO.
 */
export interface ExternalTaskSystemDetailDto extends ExternalTaskSystemDto {
  authenticationJson?: string;
  taskMappingJson?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * External task DTO.
 */
export interface ExternalTaskDto {
  id: string;
  externalTaskSystemId: string;
  externalTaskSystemName: string;
  externalTaskId: string;
  agentSessionId?: string;
  safetyGateExecutionId?: string;
  taskType: TaskType;
  status: TaskStatus;
  title: string;
  description?: string;
  assignedTo?: string;
  dueDate?: string;
  createdAt: string;
  completedAt?: string;
}

/**
 * External task detail DTO.
 */
export interface ExternalTaskDetailDto extends ExternalTaskDto {
  contextJson?: string;
  responseJson?: string;
  completedBy?: string;
}

/**
 * Create external task system request.
 */
export interface CreateExternalTaskSystemRequest {
  name: string;
  description?: string;
  systemType: string;
  apiBaseUrl: string;
  authenticationJson?: string;
  taskMappingJson?: string;
  isActive: boolean;
}

/**
 * Create external task request.
 */
export interface CreateExternalTaskRequest {
  externalTaskSystemId: string;
  taskType: TaskType;
  title: string;
  description?: string;
  agentSessionId?: string;
  safetyGateExecutionId?: string;
  contextJson?: string;
  assignedTo?: string;
  dueDate?: string;
}

/**
 * Update task status request.
 */
export interface UpdateTaskStatusRequest {
  status: TaskStatus;
  responseJson?: string;
  updatedBy?: string;
}

/**
 * Sync tasks request.
 */
export interface SyncTasksRequest {
  externalTaskSystemId: string;
}

/**
 * Sync tasks response.
 */
export interface SyncTasksResponse {
  syncedCount: number;
}

/**
 * External task system filters.
 */
export interface ExternalTaskSystemFilters {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  isActive?: boolean;
  systemType?: string;
}

/**
 * External task filters.
 */
export interface ExternalTaskFilters {
  pageNumber?: number;
  pageSize?: number;
  externalTaskSystemId?: string;
  status?: TaskStatus;
  taskType?: TaskType;
  agentSessionId?: string;
  safetyGateExecutionId?: string;
  assignedTo?: string;
  searchTerm?: string;
}

/**
 * HITL service for managing external task systems and tasks.
 */
export class HitlService extends BaseService {
  /**
   * Gets a paginated list of external task systems with optional filtering.
   */
  async getExternalTaskSystems(filters?: ExternalTaskSystemFilters): Promise<PagedResult<ExternalTaskSystemDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.systemType) queryParams.append('systemType', filters.systemType);

    const url = `/external-task-systems${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ExternalTaskSystemDto>>(url);
  }

  /**
   * Gets an external task system by ID.
   */
  async getExternalTaskSystem(id: string): Promise<ExternalTaskSystemDetailDto> {
    return this.get<ExternalTaskSystemDetailDto>(`/external-task-systems/${id}`);
  }

  /**
   * Creates a new external task system.
   */
  async createExternalTaskSystem(request: CreateExternalTaskSystemRequest): Promise<ExternalTaskSystemDetailDto> {
    return this.post<ExternalTaskSystemDetailDto>('/external-task-systems', request);
  }

  /**
   * Gets a paginated list of external tasks with optional filtering.
   */
  async getTasks(filters?: ExternalTaskFilters): Promise<PagedResult<ExternalTaskDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.externalTaskSystemId) queryParams.append('externalTaskSystemId', filters.externalTaskSystemId);
    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.taskType) queryParams.append('taskType', filters.taskType);
    if (filters?.agentSessionId) queryParams.append('agentSessionId', filters.agentSessionId);
    if (filters?.safetyGateExecutionId) queryParams.append('safetyGateExecutionId', filters.safetyGateExecutionId);
    if (filters?.assignedTo) queryParams.append('assignedTo', filters.assignedTo);
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ExternalTaskDto>>(url);
  }

  /**
   * Gets an external task by ID.
   */
  async getTask(id: string): Promise<ExternalTaskDetailDto> {
    return this.get<ExternalTaskDetailDto>(`/tasks/${id}`);
  }

  /**
   * Creates a new external task.
   */
  async createTask(request: CreateExternalTaskRequest): Promise<ExternalTaskDetailDto> {
    return this.post<ExternalTaskDetailDto>('/tasks', request);
  }

  /**
   * Updates the status of an external task.
   */
  async updateTaskStatus(id: string, request: UpdateTaskStatusRequest): Promise<ExternalTaskDetailDto> {
    return this.put<ExternalTaskDetailDto>(`/tasks/${id}/status`, request);
  }

  /**
   * Synchronizes tasks from an external task system.
   */
  async syncTasks(request: SyncTasksRequest): Promise<SyncTasksResponse> {
    return this.post<SyncTasksResponse>('/tasks/sync', request);
  }
}

export const hitlService = new HitlService();
